import React, { lazy } from 'react';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import uk from 'dayjs/locale/uk';
import relativeTime from 'dayjs/plugin/relativeTime';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
const ActionBlock = lazy(() => import('./ActionBlock'));

dayjs.extend(relativeTime);

const QuestionItem = styled('div')(({ theme, highlight }) => ({
  marginBottom: 25,
  gap: 30,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  maxWidth: '100%',
  position: 'relative',
  transition: '0.5s',
  ...(highlight === 'true'
    ? {
        backgroundColor: 'rgba(121, 201, 18, 0.1)',
        boxShadow: `0px 0px 0px 10px rgba(121, 201, 18, 0.1)`
      }
    : {}),
  [theme.breakpoints.down('md')]: {
    gap: 0
  }
}));

const WideSidebar = styled('div')(({ theme }) => ({
  flex: 1
}));

const ShortSidebar = styled('div')(({ theme, answer }) => ({
  flex: 1,
  maxWidth: 120,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  borderLeft: answer === 'true' ? 'none' : '1px solid theme.palette.grey[300]',
  borderRight: answer === 'true' ? '1px solid theme.palette.grey[300]' : 'none',
  paddingLeft: answer === 'true' ? 0 : 15,
  paddingRight: answer === 'true' ? 15 : 0,
  [theme.breakpoints.down('md')]: {
    maxWidth: 95,
    paddingLeft: answer === 'true' ? 0 : 10,
    paddingRight: answer === 'true' ? 10 : 0,
    marginLeft: answer === 'true' ? 0 : 8,
    marginRight: answer === 'true' ? 8 : 0
  }
}));

const TimeAgo = styled(Typography)(({ theme, answer }) => ({
  fontSize: 12,
  color: 'theme.palette.grey[300]',
  marginLeft: answer === 'true' ? 10 : 0,
  [theme.breakpoints.down('md')]: {
    marginLeft: answer === 'true' ? 5 : 0
  }
}));

const QuestionAuthorPhoto = styled('img')(({ theme }) => ({
  width: 35,
  height: 35,
  borderRadius: '50%',
  objectFit: 'cover',
  [theme.breakpoints.down('md')]: {
    width: 30,
    height: 30
  }
}));

const DefaultUserPhoto = styled(AccountCircleIcon)(({ theme }) => ({
  width: 35,
  height: 35,
  borderRadius: '50%',
  objectFit: 'cover',
  color: theme.palette.grey[600],
  fill: theme.palette.grey[600],
  opacity: 0.8,
  [theme.breakpoints.down('md')]: {
    width: 30,
    height: 30
  }
}));

const QuestionAuthor = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: '17px',
  fontWeight: 600,
  color: theme.palette.textPrimary.main,
  marginTop: 10,
  maxWidth: '100%'
}));

const QuestionAuthorTitle = styled(Typography)(() => ({
  lineHeight: '12px',
  marginTop: 5,
  fontSize: 12,
  color: 'theme.palette.grey[300]',
  maxWidth: '100%'
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  lineHeight: '23px',
  color: theme.palette.grey[600],
  opacity: 0.8,
  [theme.breakpoints.down('md')]: {
    fontSize: 14
  }
}));

const QuestionChildWrapper = ({
  anchor: anchorProp,
  readOnly,
  entity,
  child,
  handleCheckAuth,
  handleGiveAnswer,
  getUserRole,
  actions
}) => {
  const location = useLocation();
  const anchor = location.hash.replace('#', '') || anchorProp;
  const [highlight, setHighlight] = React.useState(false);

  React.useEffect(() => {
    if (anchor === child?._id) {
      setTimeout(() => {
        setHighlight(true);
        setTimeout(() => {
          setHighlight(false);
        }, 2500);
      }, 500);
    }
  }, [anchor, child?._id]);

  return (
    <QuestionItem key={child?._id} id={child?._id} highlight={`${highlight}`}>
      <ShortSidebar answer={'true'}>
        {child?.userAvatar ? (
          <QuestionAuthorPhoto src={child?.userAvatar} alt="author photo" />
        ) : (
          <DefaultUserPhoto />
        )}
        <QuestionAuthor>{child?.userName}</QuestionAuthor>
        <QuestionAuthorTitle>{getUserRole(child?.userStatuses)}</QuestionAuthorTitle>
      </ShortSidebar>
      <WideSidebar>
        <QuestionText>{child?.text}</QuestionText>

        <ActionBlock
          _id={child?._id}
          isAnswer={true}
          readOnly={readOnly}
          entity={entity}
          isFavorite={child?.isFavorite}
          votesTotal={child?.votesTotal}
          handleCheckAuth={handleCheckAuth}
          handleGiveAnswer={handleGiveAnswer}
          actions={actions}>
          <TimeAgo answer={'true'}>{dayjs(child?.createdAt).locale(uk).fromNow()}</TimeAgo>
        </ActionBlock>
      </WideSidebar>
    </QuestionItem>
  );
};

export default QuestionChildWrapper;
